type RecentItem = { id: string; label: string; timestamp: number };

interface RecentStoreShape {
  items: RecentItem[];
  maxItems: number;
  ttlMs: number;
}

const DEFAULT_MAX = 8;
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A recent is only worth storing/showing if its label is a real human label —
 * non-empty, not just the option's own id, and not a bare UUID. Async/server
 * pickers (e.g. ticket selectors) can momentarily build options whose `label`
 * equals the `value` (the id) before the display name resolves; without this
 * guard those raw UUIDs get persisted and later rendered in the recents list.
 */
export function isDisplayableLabel(label: unknown, id?: string): label is string {
  if (typeof label !== "string") return false;
  const trimmed = label.trim();
  if (trimmed === "") return false;
  if (id && trimmed === id) return false;
  if (UUID_RE.test(trimmed)) return false;
  return true;
}

function getStorageKey(recentKey: string) {
  return `recentSelections_${recentKey}`;
}

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getRecents(recentKey: string): RecentItem[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(getStorageKey(recentKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<RecentStoreShape> | null;
    if (!parsed || !Array.isArray(parsed.items)) return [];
    const ttlMs = typeof parsed.ttlMs === "number" ? parsed.ttlMs : DEFAULT_TTL_MS;
    const cutoff = Date.now() - ttlMs;
    const filtered = parsed.items.filter(
      (i) =>
        i &&
        typeof i.id === "string" &&
        i.timestamp >= cutoff &&
        // Purge any legacy entries whose label is a raw UUID / the id itself.
        isDisplayableLabel(i.label, i.id)
    );
    if (filtered.length !== parsed.items.length) {
      saveRecents(recentKey, filtered, parsed.maxItems ?? DEFAULT_MAX, ttlMs);
    }
    return filtered;
  } catch {
    return [];
  }
}

export function addRecent(
  recentKey: string,
  item: { id: string; label: string },
  max: number = DEFAULT_MAX
) {
  if (!isBrowser()) return;
  const existing = getRecents(recentKey);
  const prior = existing.find((i) => i.id === item.id);
  // Prefer the incoming label; if it's not displayable (e.g. a raw UUID from an
  // async picker), keep any good label we already had for this id. Never persist
  // a UUID-as-label — a recent we can't render a name for is worse than none.
  const label = isDisplayableLabel(item.label, item.id)
    ? item.label
    : prior && isDisplayableLabel(prior.label, prior.id)
      ? prior.label
      : null;
  if (!label) return;
  const deduped = existing.filter((i) => i.id !== item.id);
  const updated: RecentItem[] = [
    { id: item.id, label, timestamp: Date.now() },
    ...deduped,
  ].slice(0, Math.max(1, max));
  saveRecents(recentKey, updated, max, DEFAULT_TTL_MS);
}

export function clearRecents(recentKey: string) {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(getStorageKey(recentKey));
  } catch {
    // no-op
  }
}

function saveRecents(recentKey: string, items: RecentItem[], maxItems: number, ttlMs: number) {
  if (!isBrowser()) return;
  const payload: RecentStoreShape = { items, maxItems, ttlMs };
  try {
    window.localStorage.setItem(getStorageKey(recentKey), JSON.stringify(payload));
  } catch {
    // no-op
  }
}

export type { RecentItem };
